<template>
    <div class="container">
        <v-inline-loader v-if="isLoading"/>
        <v-title :breadcrumb="breadcrumb">
            <span>Infusionsoft</span>
            <div slot="actions" v-if="credentialHash || configuration">
                <r-button class="inline-s" v-if="credentialHash" @click="() => this.$router.push({name: 'integration-view'})">Cancel</r-button>
                <r-button type="primary" v-if="credentialHash" @click="save" :disabled="isLoading">Save</r-button>
                <r-button type="primary" v-if="configuration" @click="() => showCancelModal = true":disabled="isLoading">Delete</r-button>
            </div>
        </v-title>
        <div class="grid" v-if="!id && !configuration && !credentialHash">
            <div class="grid-item grid-item-primary">
                <div class="grid">
                    <div class="grid-item grid-item-full">
                        <div class="tile position-relative">
                            <div class="tile-title">
                                <div class="avatar no-flex">
                                    <div class="avatar-image fs-block">
                                        <figure class="image image-avatar">
                                            <img alt="Infusionsoft" src="/static/integrations/infusionsoft.png">
                                        </figure>
                                    </div>
                                </div>
                                <h2>Infusionsoft</h2>
                            </div>
                            <div class="tile-content">
                                <p class="stack-m">
                                    <span class="field-caption">Connect Rebilly with Infusionsoft.</span>
                                </p>
                                <p class="stack-m">
                                    <span class="field-caption">
                                        Connect powerful CRM with advanced sales and marketing automation, fully-customizable campaigns, daily tasks & appointments with Rebilly.
                                    </span>
                                </p>
                                <p class="stack-m">
                                    <span class="field-caption">
                                        Questions? Read more about the Infusionsoft integration in our documentation.
                                    </span>
                                </p>
                                <r-button class="inline-s" type="primary" @click="createOAuth2Credentials" :loading="isLoading">
                                    Connect to Infusionsoft
                                </r-button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <div class="grid-item grid-item-secondary">
                <integrations-read-more :integration="integration" />
            </div>
        </div>
        <div v-else>
            <v-form-tile>
                <h2 slot="title">About Infusionsoft Integration</h2>
                <div slot="description">
                    <p class="stack-m">We will sync all your orders and related data with Rebilly without any additional effort from you.</p>
                </div>
                <div slot="content" class="tile">
                    <div class="tile-content">
                        <table class="table">
                            <tbody>
                                <tr>
                                    <td>
                                        <img class="image image-32 no-margin-bottom" src="/static/gateways/RebillyProcessor.png"/>
                                    </td>
                                    <td></td>
                                    <td>
                                        <img class="image image-32 no-margin-bottom" src="/static/integrations/infusionsoft.png"/>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                        <table class="table table-dark border-radius stack-m">
                            <thead>
                            <tr>
                                <th>Rebilly term</th>
                                <th>Maps to</th>
                                <th>On Infusionsoft</th>
                            </tr>
                            </thead>
                            <tbody>
                            <tr>
                                <td>Customers</td>
                                <td><v-icon icon="arrow-right"/></td>
                                <td>Contacts</td>
                            </tr>
                            <tr>
                                <td>Invoices</td>
                                <td><v-icon icon="arrow-right"/></td>
                                <td>Orders</td>
                            </tr>
                            <tr>
                                <td>Transaction</td>
                                <td><v-icon icon="arrow-right"/></td>
                                <td>Payments</td>
                            </tr>
                            </tbody>
                        </table>
                        <p class="stack-m">
                            <r-badge type="info">Your Rebilly activity will sync over to Infusionsoft</r-badge>
                        </p>
                    </div>
                </div>
            </v-form-tile>
            <div class="divider"></div>
            <v-form-tile>
                <h2 slot="title">Advanced Configuration</h2>
                <div slot="description">
                    <p class="stack-m">If you need a more flexible solution than the provided default</p>
                </div>
                <div slot="content" class="tile position-relative">
                    <div class="tile-content">
                        <p>If you require an advanced configuration please <a href="https://www.rebilly.com/support/">contact us</a>.</p>
                    </div>
                </div>
            </v-form-tile>
        </div>
        <v-modal v-if="showCancelModal" title="Remove connection" @close="closeModal" size="small">
            <div slot="content">
                <p>Are you sure you want to remove this connection?</p>
            </div>
            <template slot="actions">
                <r-button type="danger" @click="removeIntegration">Remove connection</r-button>
            </template>
        </v-modal>
    </div>
</template>

<script>
    import vTitle from '@/components/v-title';
    import {
        vFormTile,
        vIcon,
        vErrorMessages,
    } from '@/components/form';
    import vModal from '@/components/modal/v-modal';
    import EventTypes from '@/common/api/definitions/event-types';
    import vInlineLoader from '@/components/v-inline-loader';
    import RuleSetModel from '@/common/models/rules/rule-set-model';
    import IntegrationModel from '@/common/models/integration-model';
    import ConnectionWindow from '@/modules/integrations/connection-window';
    import IntegrationsReadMore from '@/modules/integrations/components/integrations-read-more';
    import {
        buildInvoiceIssuedBind,
        buildInvoicePaidBind,
    } from '@/modules/integrations/keap-infusionsoft/keap-infusionsoft-handlers';

    // noinspection JSUnusedGlobalSymbols
    export default {
        components: {
            vModal,
            vTitle,
            vFormTile,
            vIcon,
            vErrorMessages,
            vInlineLoader,
            IntegrationsReadMore,
        },
        props: {
            integration: {
                type: IntegrationModel,
                required: true,
            },
            configuration: {
                type: Object,
            },
            id: {
                type: String,
            },
        },
        data() {
            return {
                isLoading: false,
                credentialHash: null,
                showCancelModal: false,
                integrationPrefix: IntegrationModel.Services.keapInfusionsoft,
                connectionWindow: new ConnectionWindow({window}),
            };
        },
        computed: {
            breadcrumb() {
                const route = this.id
                    ? {name: 'integration-edit', params: {service: this.integration.service, id: this.id}}
                    : {name: 'integration-add', params: {service: this.integration.service}};

                return {label: this.pageTitle, route};
            },
        },
        methods: {
            closeModal() {
                this.showCancelModal = false;
            },
            stopLoadingPage() {
                this.isLoading = false;
            },
            async removeIntegration() {
                this.isLoading = true;
                this.showCancelModal = false;
                const {eventType} = this.configuration;
                const ruleSet = await this.fetchRuleSet(eventType);
                ruleSet.removeIntegration(this.integrationPrefix);
                await this.$api(({events}) => events.updateRules({
                    eventType,
                    data: ruleSet.getPayload(),
                }));
                this.$toast.positive('Integration was removed');
                this.$router.push({name: 'integration-view'});
            },
            async fetchRuleSet(eventType) {
                const {fields: ruleSet} = await this.$api(({events}) => events.getRules({eventType}));
                return new RuleSetModel(ruleSet);
            },
            async save() {
                this.isLoading = true;
                try {
                    const invoiceIssuedBind = buildInvoiceIssuedBind(this);
                    const invoicePaidBind = buildInvoicePaidBind(this);
                    const [ruleSetInvoiceIssued, ruleSetInvoicePaid] = await Promise.all([
                        this.fetchRuleSet(EventTypes.invoiceIssued),
                        this.fetchRuleSet(EventTypes.invoicePaid),
                    ]);
                    ruleSetInvoiceIssued.addIntegration(invoiceIssuedBind);
                    ruleSetInvoicePaid.addIntegration(invoicePaidBind);
                    await Promise.all([
                        this.$api(({events}) => events.updateRules({
                            eventType: EventTypes.invoiceIssued,
                            data: ruleSetInvoiceIssued.getPayload(),
                        })),
                        this.$api(({events}) => events.updateRules({
                            eventType: EventTypes.invoicePaid,
                            data: ruleSetInvoicePaid.getPayload(),
                        })),
                    ]);
                    this.$toast.positive('You are connected');
                    this.$router.push({name: 'integration-view'});
                } catch (error) {
                    this.$toast.fromError(error);
                }
                this.isLoading = false;
            },
            async createOAuth2Credentials() {
                this.isLoading = true;

                // To debug the callback from Infusionsoft, open callback URL in the popup with needed query params.
                // https://signin.infusionsoft.com/app/oauth/authorize?client_id=vkw6tgmgv93zqtb83f7jysrd&redirect_uri=https://app.dev-local.rebilly.com:8080%2Fintegrations%2Fkeap-infusionsoft%2Fcallback&response_type=code&scope=full
                const {href: oauth2ConnectUrl} = this.integration.oauth2ConnectUrl;
                await this.connectionWindow
                    .sendRequest({url: oauth2ConnectUrl})
                    .then(async ({scope, code}) => {
                        const data = {
                            service: this.integration.service,
                            scopes: [...scope.split(' ')],
                            code,
                        };
                        // Mock credential, to debug the component, w/o valid code.
                        // this.credentialHash = {...data}.hash || data.code;
                        const {fields} = await this.$api(
                            ({credentialHashes}) => credentialHashes.createOAuth2Credential({data}),
                        );
                        this.credentialHash = fields.hash;
                    })
                    .catch((error) => {
                        this.$toast.fromError(error);
                    })
                    .finally(this.stopLoadingPage);
            },
        },
        beforeDestroy() {
            this.connectionWindow.close();
        },
    };
</script>
